import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  apiEndPoints,
  getAssignableUsersEndPoint,
} from '../../common/api.endPoints';
import { FetchService } from '../../common/fetch.service';
import { AssignmentReportModal } from './AssignmentReportModal';
import {
  ReviewBulkAssignmentModal,
  ValidationResponse,
} from './ReviewBulkAssignmentModal';

type AssignmentType = 'underwriter' | 'casemanager';

interface AssignableUser {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  active: boolean;
  paused: boolean;
}

interface ExecutionResponse {
  assigned: number;
  skipped: number;
  assignedCarrierNumbers: string[];
  failed: { carrierPolicyNumber: string; reason: string; hint: string }[];
}

interface BulkCaseAssignmentRequestBody {
  assignmentType: AssignmentType;
  carrierPolicyNumbers: string[];
  conflictPolicyNumbers?: string[];
  assigneeUserId: number;
}

const ASSIGNMENT_TYPE_LABELS: Record<AssignmentType, string> = {
  underwriter: 'Underwriter Assignment',
  casemanager: 'Case Manager Assignment',
};

const GENERIC_SERVER_ERROR = 'Something went wrong. Please try again.';

/**
 * 5xx -> generic message (strict failure path: main-app lookup down, DB down, etc.).
 * 4xx -> server-provided message (validation errors, missing inputs, etc.).
 */
function pickErrorMessage(err: any, fallback: string): string {
  const status = err?.response?.status ?? err?.status;
  if (typeof status === 'number' && status >= 500) {
    return GENERIC_SERVER_ERROR;
  }
  return err?.response?.data?.message || err?.message || fallback;
}

// Splits the textarea content on commas / newlines / whitespace and dedupes.
// Carrier policy numbers can be alphanumeric, so we keep tokens as-is.
function parseCarrierNumbers(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,\n\s]+/)) {
    const t = raw.trim();
    if (t.length > 0 && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

function userLabel(u: AssignableUser): string {
  return (
    u.displayName ||
    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
    u.email
  );
}

export function BulkCaseAssignment() {
  const [assignmentType, setAssignmentType] =
    useState<AssignmentType>('underwriter');
  const [carrierNumbersText, setCarrierNumbersText] = useState('');
  const [assignee, setAssignee] = useState<AssignableUser | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<ExecutionResponse | null>(
    null,
  );
  const [reportOpen, setReportOpen] = useState(false);
  const [reportAssignee, setReportAssignee] = useState<{
    name: string;
    initials: string;
  } | null>(null);

  // Switching role types means the assignee dropdown changes — clear the picked user.
  useEffect(() => {
    setAssignee(null);
  }, [assignmentType]);

  const {
    data: users = [],
    isLoading: loadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['bulk-case-assignment-users', assignmentType],
    queryFn: async () => {
      const endpoint = getAssignableUsersEndPoint(assignmentType);
      const result: AssignableUser[] =
        await FetchService.getInstance().get(endpoint);
      return result || [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const validateMutation = useMutation({
    mutationFn: async (body: BulkCaseAssignmentRequestBody) => {
      const response: ValidationResponse =
        await FetchService.getInstance().post(
          apiEndPoints.bulkCaseAssignmentValidate,
          { body },
        );
      return response;
    },
    onSuccess: data => {
      setValidation(data);
      setReviewOpen(true);
    },
    onError: (err: any) => {
      setSubmitError(
        pickErrorMessage(err, 'Failed to validate bulk assignment'),
      );
    },
  });

  const executeMutation = useMutation({
    mutationFn: async (body: BulkCaseAssignmentRequestBody) => {
      const response: ExecutionResponse = await FetchService.getInstance().post(
        apiEndPoints.bulkCaseAssignmentExecute,
        { body },
      );
      return response;
    },
    onSuccess: (data, _vars, _ctx) => {
      setReviewOpen(false);
      setSuccessInfo(data);
      setReportOpen(false);
      // Save assignee info before validation is cleared — needed by the report modal.
      setReportAssignee(
        validation
          ? {
              name: validation.assignee.name,
              initials: validation.assignee.initials,
            }
          : null,
      );
      setCarrierNumbersText('');
      setAssignee(null);
      setValidation(null);
    },
    onError: (err: any) => {
      setSubmitError(pickErrorMessage(err, 'Failed to assign cases'));
    },
  });

  const parsedCarrierNumbers = useMemo(
    () => parseCarrierNumbers(carrierNumbersText),
    [carrierNumbersText],
  );
  const canReview =
    assignee !== null &&
    parsedCarrierNumbers.length > 0 &&
    !validateMutation.isLoading;

  /** Body for the validate (dry-run) call — uses the full user input. */
  const buildValidateBody = (): BulkCaseAssignmentRequestBody | null => {
    if (!assignee || parsedCarrierNumbers.length === 0) return null;
    return {
      assignmentType,
      carrierPolicyNumbers: parsedCarrierNumbers,
      assigneeUserId: assignee.id,
    };
  };

  /**
   * Body for the execute call. `carrierPolicyNumbers` contains the ready set
   * from validation. When `includeConflicts` is true, `conflictPolicyNumbers`
   * is populated with the conflict-bucket carrier numbers the manager approved
   * in the review modal.
   */
  const buildExecuteBody = (
    includeConflicts: boolean,
  ): BulkCaseAssignmentRequestBody | null => {
    if (!assignee || !validation) return null;
    const conflictPolicyNumbers = includeConflicts
      ? validation.skipped
          .filter(s => s.bucket === 'conflict')
          .map(s => s.carrierPolicyNumber)
      : [];
    if (validation.ready.length === 0 && conflictPolicyNumbers.length === 0)
      return null;
    return {
      assignmentType,
      carrierPolicyNumbers: validation.ready.map(r => r.carrierPolicyNumber),
      ...(conflictPolicyNumbers.length > 0 && { conflictPolicyNumbers }),
      assigneeUserId: assignee.id,
    };
  };

  const handleReview = () => {
    setSubmitError(null);
    setSuccessInfo(null);
    if (parsedCarrierNumbers.length === 0) {
      setSubmitError('Enter at least one carrier policy number');
      return;
    }
    if (!assignee) {
      setSubmitError('Select a user to assign to');
      return;
    }
    const body = buildValidateBody();
    if (body) validateMutation.mutate(body);
  };

  const handleProceed = (includeConflicts: boolean) => {
    const body = buildExecuteBody(includeConflicts);
    if (body) executeMutation.mutate(body);
  };

  const handleCancelReview = () => {
    if (executeMutation.isLoading) return;
    setReviewOpen(false);
  };

  const handleReset = () => {
    setCarrierNumbersText('');
    setAssignee(null);
    setSubmitError(null);
    setSuccessInfo(null);
    setValidation(null);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h1" gutterBottom sx={{ mb: 3 }}>
        Bulk Case Assignment
      </Typography>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="bulk-case-assignment-type-label">
              Assignment Type
            </InputLabel>
            <Select
              labelId="bulk-case-assignment-type-label"
              value={assignmentType}
              label="Assignment Type"
              onChange={e =>
                setAssignmentType(e.target.value as AssignmentType)
              }
            >
              <MenuItem value="underwriter">Underwriter Assignment</MenuItem>
              <MenuItem value="casemanager">Case Manager Assignment</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Carrier Policy Numbers"
            multiline
            rows={6}
            value={carrierNumbersText}
            onChange={e => setCarrierNumbersText(e.target.value)}
            placeholder="Comma or newline-separated. Example: 4001403796, 4003184600"
            helperText="Enter the carrier policy numbers to assign."
          />

          <Autocomplete
            options={users}
            getOptionLabel={userLabel}
            value={assignee}
            onChange={(_, v) => setAssignee(v)}
            disabled={loadingUsers}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={params => (
              <TextField
                {...params}
                label="Assign to user"
                placeholder="Search users..."
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleReview}
              disabled={!canReview}
              startIcon={
                validateMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : null
              }
              sx={{ minWidth: 200 }}
            >
              {validateMutation.isLoading ? 'Reviewing...' : 'Review & Assign'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleReset}
              disabled={validateMutation.isLoading || executeMutation.isLoading}
            >
              Reset Form
            </Button>
          </Box>
        </Box>

        {(loadingUsers || usersError || submitError || successInfo) && (
          <Box sx={{ mt: 3 }}>
            {loadingUsers && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                <Typography variant="body2">
                  Loading{' '}
                  {assignmentType === 'underwriter'
                    ? 'underwriters'
                    : 'case managers'}
                  ...
                </Typography>
              </Alert>
            )}
            {usersError && !loadingUsers && (
              <Alert severity="warning">
                Failed to load assignable users. Please refresh the page.
              </Alert>
            )}
            {submitError && (
              <Alert severity="error">
                <Typography variant="body2">{submitError}</Typography>
              </Alert>
            )}
            {successInfo && (
              <Alert
                severity={
                  successInfo.assigned === 0 && successInfo.failed.length > 0
                    ? 'warning'
                    : 'success'
                }
              >
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  Bulk assignment{' '}
                  {successInfo.assigned === 0 && successInfo.failed.length > 0
                    ? 'completed with failures'
                    : 'successful'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{successInfo.assigned}</strong> case
                  {successInfo.assigned === 1 ? '' : 's'} assigned.
                  {successInfo.failed.length > 0 &&
                    ` ${successInfo.failed.length} polic${successInfo.failed.length === 1 ? 'y' : 'ies'} failed assignment.`}
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setReportOpen(true)}
                  sx={{
                    fontSize: 12,
                    padding: '4px 12px',
                    borderColor: 'currentColor',
                    opacity: 0.75,
                    '&:hover': { opacity: 1, backgroundColor: 'transparent' },
                  }}
                >
                  View assignment report
                </Button>
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {validation && (
        <ReviewBulkAssignmentModal
          open={reviewOpen}
          validation={validation}
          assignmentTypeLabel={ASSIGNMENT_TYPE_LABELS[assignmentType]}
          isProceeding={executeMutation.isLoading}
          onCancel={handleCancelReview}
          onProceed={handleProceed}
        />
      )}

      {successInfo && reportAssignee && (
        <AssignmentReportModal
          open={reportOpen}
          assignedCarrierNumbers={successInfo.assignedCarrierNumbers ?? []}
          failed={successInfo.failed}
          assignee={reportAssignee}
          assignmentTypeLabel={ASSIGNMENT_TYPE_LABELS[assignmentType]}
          onClose={() => setReportOpen(false)}
        />
      )}
    </Box>
  );
}
